import {Component, OnInit, ViewChild} from '@angular/core';
import {StripePaymentElementComponent, StripeService} from 'ngx-stripe';
import {StripeElementsOptions} from '@stripe/stripe-js';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ResponseSnackbarService} from '../../../../shared/services/response-snackbar.service';
import {GeneralConstant} from '../../../../shared/constants/GeneralConstant';
import {PaymentMethodService} from '../../../../shared/services/payment-method.service';
import {ConfirmModalComponent} from '../../../shared/modules/confirm-modal/confirm-modal.component';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-stripe-add-bank-account',
  templateUrl: './stripe-add-bank-account.component.html',
  styleUrls: ['./stripe-add-bank-account.component.scss']
})
export class StripeAddBankAccountComponent implements OnInit {
  @ViewChild(StripePaymentElementComponent) paymentElement: StripePaymentElementComponent;

  elementsOptions: StripeElementsOptions = {
    locale: 'en'
  };

  appearance = {
    theme: 'stripe',
    labels: 'floating'
  };

  saveBtnLoading = false;
  confirmBtnDisabled = true;
  clientSecret: string;
  verificationUrl: string;

  constructor(
    private paymentMethodService: PaymentMethodService,
    private stripeService: StripeService,
    public dialogRef: MatDialogRef<StripeAddBankAccountComponent>,
    private snackBar: ResponseSnackbarService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.createSetupIntent();
  }

  cancel(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        fullName: 'Warning',
        name: '',
        type: 'Confirm',
        removeQuestionMark: true,
        content: 'Are you sure you want to cancel adding this bank account?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.delete) {
        this.dialogRef.close();
      }
    });
  }

  onChange(event: any): void {
    this.confirmBtnDisabled = !event.complete;
  }

  onSubmit(): void {
    if (!this.paymentElement?.elements || this.confirmBtnDisabled) {
      return;
    }

    this.saveBtnLoading = true;
    this.confirmBtnDisabled = true;

    this.stripeService.confirmSetup({
      elements: this.paymentElement.elements,
      redirect: 'if_required'
    }).pipe(
      finalize(() => {
        this.saveBtnLoading = false;
        this.confirmBtnDisabled = false;
      })
    ).subscribe(result => {
      if (result.error) {
        this.snackBar.onResponseMessage(result.error.message, GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
        return;
      }

      const setupIntent = result.setupIntent;
      const nextAction = setupIntent?.next_action as any;
      if (setupIntent?.status === 'requires_action'
        && nextAction?.type === 'verify_with_microdeposits'
        && nextAction?.verify_with_microdeposits?.hosted_verification_url) {
        this.verificationUrl = nextAction.verify_with_microdeposits.hosted_verification_url;
        this.snackBar.onResponseMessage(
          'Bank account added. Complete verification to start using it.',
          GeneralConstant.SNACKBAR_RES_SUCCESS_CLASS
        );
        return;
      }

      if (setupIntent?.status === 'succeeded') {
        this.snackBar.onResponseMessage(
          'Bank account saved successfully.',
          GeneralConstant.SNACKBAR_RES_SUCCESS_CLASS
        );
        this.dialogRef.close(true);
      }
    });
  }

  openVerificationUrl(): void {
    if (this.verificationUrl) {
      window.open(this.verificationUrl, '_blank');
      this.dialogRef.close(true);
    }
  }

  private createSetupIntent(): void {
    this.saveBtnLoading = true;
    this.paymentMethodService.createSetupIntent('us_bank_account').pipe(
      finalize(() => {
        this.saveBtnLoading = false;
      })
    ).subscribe(response => {
      this.clientSecret = response.clientSecret;
    }, error => {
      const message = error?.error?.message || 'Unable to start bank account setup.';
      this.snackBar.onResponseMessage(message, GeneralConstant.SNACKBAR_RES_ERROR_CLASS);
      this.dialogRef.close();
    });
  }
}
